/**
 * PillarCompliance — the Baseline Compliance tile on the Command Center
 * (mockup 01 pillar row).
 *
 * Data:
 *   - DashboardStats → the baseline score, open drifts and their severity mix.
 *   - usePriorityActions(compliance) → the ranked top item, named in the
 *     finding ("{Title} first.").
 *
 * The visualization is the severity meter (§7 C11, D30): per-workload drift
 * counts need a backend change (F4), so the tile draws the open drifts by
 * severity from `issuesBySeverity`. Counts are resources with drift (§7 C10).
 *
 * Unknown is never zero: a failed stats call (`activeDriftsKnown === false`)
 * reads as "did not load", a tenant with nothing checked yet shows the
 * designed empty state, and an under-captured baseline shows no score.
 */

import type { ReactNode } from 'react'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import { SegmentedMeter, Skeleton } from '@/components/carbon'
import type { DashboardStats } from '@/types'
import { ComplianceEmpty } from './EmptyStates'
import { PillarTile } from './PillarShell'
import { knownBaselinePct } from './cc-score'
import { firstStep, plural } from './cc-queue'

interface Props {
  tenantId: string | undefined
  stats: DashboardStats | null | undefined
  isLoading: boolean
  /** The delta tag ("+2 in 90 days"), from the score history (cc-history.ts). */
  delta?: ReactNode
}

const TITLE = 'Baseline Compliance'

export default function PillarCompliance({ tenantId, stats, isLoading, delta }: Props) {
  // Server-ranked drift-source priority actions; the first names the next step.
  // Pillar-filtered so cross-pillar items never leak into this tile.
  const { actions, error: actionsError } = usePriorityActions({
    tenantId,
    maxCount: 3,
    pillar: 'compliance',
  })

  const failed = stats != null && stats.activeDriftsKnown === false
  const pct = knownBaselinePct(stats)
  const nothingChecked = !isLoading && !failed && stats != null && pct == null && !(stats.activeDrifts > 0)
  const incomplete = stats?.complianceScore?.baselineIncomplete ?? false
  const score = incomplete ? null : pct
  const active = stats?.activeDriftsKnown ? stats.activeDrifts : null
  const pending = stats?.pendingProcessing ?? 0
  const sev = stats?.issuesBySeverity
  const next = actionsError ? null : firstStep(actions[0]?.title)

  if (nothingChecked) {
    return (
      <PillarTile
        title={TITLE}
        category="baseline"
        to="/configuration-drift"
        linkLabel="Open Baseline Compliance"
        score={null}
        delta={delta}
      >
        <ComplianceEmpty />
      </PillarTile>
    )
  }

  let finding: string | null = null
  if (failed) {
    finding = 'Baseline data did not load. Refresh the page to try again.'
  } else if (active != null && incomplete) {
    finding = `Guardian is still capturing this tenant's baseline, so there is no score yet. ${plural(active, 'resource has', 'resources have')} drifted so far.`
  } else if (active != null && active > 0) {
    finding = `${plural(active, 'resource has', 'resources have')} drifted from your baseline.${next ? ` ${next}` : ''}`
  } else if (active === 0 && pending > 0) {
    finding = `No drift needs a decision. ${plural(pending, 'accepted change is', 'accepted changes are')} ready to commit to the baseline.`
  } else if (active === 0) {
    finding = 'Nothing to review. Every watched resource matches your baseline.'
  }

  const ariaLabel = [score == null ? TITLE : `${TITLE}, ${Math.round(score)}`, finding].filter(Boolean).join('. ')

  return (
    <PillarTile
      title={TITLE}
      category="baseline"
      to="/configuration-drift"
      linkLabel="Open Baseline Compliance"
      score={score}
      isLoading={isLoading}
      delta={delta}
      finding={isLoading ? <Skeleton lines={2} /> : finding}
      ariaLabel={isLoading ? TITLE : ariaLabel}
      viz={
        isLoading || failed || !sev ? null : (
          <SegmentedMeter
            caption="Open drifts by severity"
            segments={[
              { id: 'crit', value: sev.critical, tone: 'crit', label: 'critical', glyph: true },
              { id: 'high', value: sev.high, tone: 'high', label: 'high', glyph: true },
              { id: 'med', value: sev.medium, tone: 'med', label: 'medium', glyph: true },
              { id: 'low', value: sev.low, tone: 'low', label: 'low', glyph: true },
            ]}
          />
        )
      }
    />
  )
}
